'use client';

import React from 'react';
import styled from 'styled-components';
import { COLORS } from '@/constants/colors';
import type { CmsTableBlock } from '@/lib/cms';

/**
 * Renders a CMS "table" content block. Deliberately generic over column
 * count (not a fixed "A vs B" shape) so it covers both a 2-column
 * comparison table and a wide multi-column spec table with the same
 * component, matching the visual style already used across the site's
 * hand-authored blog posts (e.g. the Subaru WRX / Tacoma comparison tables).
 */
export default function CmsTable({ block }: { block: CmsTableBlock }) {
  return (
    <>
      {block.heading && <SectionHeading>{block.heading}</SectionHeading>}
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              {block.headers.map((header, i) => (
                <TableHeaderCell key={i}>{header.label}</TableHeaderCell>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.cells.map((cell, cellIndex) => (
                  <TableCell key={cellIndex} $isLast={cellIndex === row.cells.length - 1}>
                    {cell.value}
                  </TableCell>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
    </>
  );
}

const SectionHeading = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 48px 0 24px;
  line-height: 1.3;

  @media (max-width: 767px) {
    font-size: 26px;
    margin: 40px 0 20px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin: 24px 0;
  border: 1px solid #e9ecef;
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 480px;
`;

const TableHeaderCell = styled.th`
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.white};
  background: ${COLORS.brandGreen};
  text-align: left;
  padding: 12px 16px;
`;

const TableCell = styled.td<{ $isLast: boolean }>`
  font-family: Lato, sans-serif;
  font-size: 15px;
  color: #555;
  padding: 12px 16px;
  border-top: 1px solid #e9ecef;
  font-weight: ${(props) => (props.$isLast ? 600 : 400)};
  color: ${(props) => (props.$isLast ? '#1a1a1a' : '#555')};
`;
